import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ListChecks, Plus } from 'lucide-react';
import { format } from 'date-fns';

import PageHeader from '../components/PageHeader.jsx';
import { listSales } from '../api/sales';

/**
 * Sales list — paginated, newest first.
 *
 * Each row is a tappable card showing the invoice number, customer, date,
 * status, and total. Tapping navigates to /sales/:id for the proforma /
 * full detail. A floating "+ New" button at the bottom-right is the entry
 * to the create flow (mirrors common mobile FAB pattern).
 *
 * Page-size 20; "Load more" extends the list rather than paginating with
 * page numbers (mobile-natural).
 */

// Backend enum (backend/app/models/sale.py): draft | delivered | paid |
// write_off | cancelled. Pill colours signal stage, not severity:
//   - draft:     neutral grey, sale is in progress
//   - delivered: green, stock is out of the door
//   - paid:      green, money has landed
//   - write_off: orange, finalised loss (kept distinct from cancelled)
//   - cancelled: red, voided before completion
const STATUS_PILL = {
  draft:      'bg-gray-100 text-gray-700',
  delivered:  'bg-green-100 text-green-700',
  paid:       'bg-green-100 text-green-800',
  write_off:  'bg-orange-100 text-orange-800',
  cancelled:  'bg-red-100 text-red-700',
};

// Filter chips above the list — only the three lifecycle stages a rep
// cares about while working in the field. Terminal states (write_off,
// cancelled) and the "All" chip are gone; instead, tapping an already-
// active chip toggles it off and the list goes back to unfiltered.
// Page-load default is unfiltered (no chip selected) so reps see every
// sale unless they explicitly narrow.
const STATUS_FILTERS = [
  { value: 'draft',     label: 'Draft'     },
  { value: 'delivered', label: 'Delivered' },
  { value: 'paid',      label: 'Paid'      },
];

function StatusPill({ status }) {
  const cls = STATUS_PILL[status] ?? 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-pill text-caption font-medium capitalize ${cls}`}>
      {status?.replace(/_/g, ' ') ?? 'unknown'}
    </span>
  );
}

function formatMoney(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return format(new Date(iso), 'd MMM yyyy');
  } catch {
    return '';
  }
}

export default function Sales() {
  const navigate = useNavigate();
  // Default to Draft on first land — that's the most-active view for
  // reps in the field (in-progress sales they're still building).
  // Tap-to-deselect on the active chip clears the filter back to "all
  // sales" if they want a wider view.
  const [statusFilter, setStatusFilter] = useState('draft');
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  // Re-fetch from page 1 whenever the status filter changes. We don't
  // accumulate the prior list — switching filters always starts fresh.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listSales({
      page: 1,
      pageSize: 20,
      status: statusFilter ?? undefined,
    })
      .then((data) => {
        if (cancelled) return;
        setItems(data.items ?? []);
        setHasMore((data.page ?? 1) < (data.pages ?? 0));
        setPage(data.page ?? 1);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.detail || err.message || 'Failed to load sales');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [statusFilter]);

  async function loadMore() {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const next = page + 1;
      const data = await listSales({
        page: next,
        pageSize: 20,
        status: statusFilter ?? undefined,
      });
      setItems((curr) => [...curr, ...(data.items ?? [])]);
      setHasMore((data.page ?? next) < (data.pages ?? 0));
      setPage(data.page ?? next);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  }

  // Pick up any flash message dropped here via router state — e.g. by
  // SaleDetail after a successful delete, which navigates here with a
  // confirmation. Same pattern as SaleDetail/QuoteDetail use for their
  // own incoming flashes.
  const location = useLocation();
  const flash = location.state?.flash;

  return (
    <main className="min-h-full px-4 pb-24">
      <PageHeader title="Sales" subtitle="Recent invoices, newest first" icon={ListChecks} />

      {flash && (
        <div role="status" className="mb-3 mb-card bg-green-50 border-green-200 text-small text-green-900">
          {flash}
        </div>
      )}

      {/* Status filter chips. Horizontal scroll on narrow phones so all
          options stay reachable without wrapping into multiple rows. The
          `-mx-4 px-4` lets chips scroll past the page's side padding while
          the active chip stays visually anchored.

          Tapping an active chip toggles it off — that's the only way
          back to the "all sales" view since the explicit All chip is
          gone. */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-3 -mx-4 px-4">
        {STATUS_FILTERS.map((f) => {
          const active = statusFilter === f.value;
          return (
            <button
              key={f.label}
              type="button"
              onClick={() => setStatusFilter(active ? null : f.value)}
              // Solid amber-500 when active — matches the brand
              // vocabulary used by PageHeader, the FAB, the Save bar,
              // and the Mark-Delivered button so filter selection
              // reads as "this is the same product." Tap-to-toggle
              // keeps the "show all" path open without a dedicated
              // All chip.
              className={`min-h-touch px-4 rounded-pill text-small font-medium whitespace-nowrap transition-colors ${
                active
                  ? 'bg-amber-500 text-white border border-amber-500'
                  : 'bg-surface border border-gray-300 text-gray-700'
              }`}
              aria-pressed={active}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {error && (
        <p role="alert" className="mb-3 text-small text-danger">
          {error}
        </p>
      )}

      {loading ? (
        <p className="text-small text-gray-500">Loading sales…</p>
      ) : items.length === 0 ? (
        <div className="mb-card text-center py-8">
          <p className="text-body text-gray-700 font-medium">No sales yet</p>
          <p className="mt-1 text-small text-gray-500">
            Tap the + button to create your first invoice.
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((s) => (
            <li key={s.id}>
              <Link
                to={`/sales/${s.id}`}
                className="mb-card-tap block no-underline"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-body font-semibold text-gray-900 truncate">
                      {s.invoice_number ?? `#${s.id}`}
                    </p>
                    <p className="text-small text-gray-700 truncate">
                      {s.customer_name ?? '—'}
                    </p>
                    <p className="mt-1 text-caption text-gray-500">
                      {formatDate(s.date)}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <StatusPill status={s.status} />
                    <p className="text-body font-semibold text-gray-900">
                      {formatMoney(s.total_amount)}
                    </p>
                    {Number(s.outstanding_amount) > 0 && (
                      <p className="text-caption text-amber-700">
                        {formatMoney(s.outstanding_amount)} due
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {hasMore && (
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="min-h-touch px-4 text-small text-amber-700 font-medium hover:underline disabled:opacity-60"
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}

      {/* Floating "+ New" button — mobile-native FAB pattern.
          Sits above the safe-area inset so it doesn't get hidden behind
          the iOS home indicator. */}
      <button
        type="button"
        onClick={() => navigate('/sales/new')}
        aria-label="New sale"
        className="fixed right-4 bottom-6 mb-safe-bottom h-14 w-14 rounded-full bg-amber-500 text-white shadow-lg flex items-center justify-center active:scale-95 transition-transform"
        style={{ bottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
      >
        <Plus className="h-6 w-6" />
      </button>
    </main>
  );
}
